import { IReceipt, ReceiptRow } from '@interfaces/models/receipt.interface';
import { IOpenAccountState } from '@interfaces/state';
import {
  CountryUS,
  DecedentInfoState,
  IRATrustTypesMapper,
  ISelectedClientAccountsState,
  stateFullNameMap,
  stateToFormNameMap,
  TaxUploadOptionKey,
  taxUploadOptionsKeys,
  TransactionTypesEnum,
  urlMapper,
} from '@shared/models';
import { ReceiptModal } from '@shared/models/receipt-modal';
import { TrustNumber } from '@shared/models/trust';
import { TransactionState } from '@store/reducers';
import { IDocumentUploadState } from '@store/reducers/document-upload/document-upload.reducer';
import { EstateState } from '@store/reducers/estate/estate.reducer';
import { ITaxDocumentUploadState } from '@store/reducers/tax-document-upload/tax-document-upload.reducer';
import {
  getTCFTrusteeInformation,
  TCFTrustState,
} from '@store/reducers/tcf-trust/tcf-trust.reducer';
import {
  getIsDecedentMarried,
  getIsInheritanceFlow,
  getIsIRAExecutor,
  getIsIRASingleExecutor,
  getIsIRATrust,
  getIsMultiInheritanceFlow,
  getIsSingleExecutor,
  getIsTrustToTrustFlow,
  TriageState,
} from '@store/reducers/triage/triage.reducer';
import { TrustState } from '@store/reducers/trust/trust.reducer';
import { TrustTaxInfoState } from '@store/reducers/trust-tax-info/trust-tax-info.reducer';
import { isNJOrPAEligible } from '@store/selectors/router/multi-inheritance/multi-inheritance-router.helpers';
import * as dayjs from 'dayjs';

import { joinWithSpace } from '../cashier-nonretirement';
import { safeRowBuilder, splitClientId } from '../new-account/new-account.cashier';
import {
  buildL8ITWDocumentsRow,
  buildSettlementFundRow,
  buildWldDocumentDisplayName,
} from '../receipt.helpers';

export function buildDocumentUploadReceipt(
  documentUploadState: IDocumentUploadState,
  taxDocumentUploadState: ITaxDocumentUploadState,
  inheritanceWaiverState: string,
  currentTransactionState: TransactionState | null | undefined,
  editable: boolean = true,
  hasClientUploadedL8File: boolean = false,
): IReceipt | null {
  // Coerce null to undefined to match isNJOrPAEligible signature
  const currentTransaction = currentTransactionState ?? undefined;
  let additionalText = '';

  const hasDocumentUploads = documentUploadState.filesMetaData.length > 0;
  const hasTaxDocumentUploads = taxDocumentUploadState.filesMetaData.length > 0;
  // NOTE: wldLetterDocuments (letter of account approval) is intentionally excluded here.
  // The letter belongs to the Employment section ("Account approval letter"), not Legal documents.
  const allWldDocuments = [
    ...(documentUploadState.wldStandardDocuments ?? []),
    ...(documentUploadState.wldTaxDocuments ?? []),
  ];
  const hasWldDocuments = allWldDocuments.length > 0;
  const hasCheckboxSelections = Object.values(
    taxDocumentUploadState?.checkboxSelections || {},
  ).some((value) => value === true);

  if (!hasDocumentUploads && !hasTaxDocumentUploads && !hasWldDocuments && !hasCheckboxSelections) {
    return null;
  }

  const formName: string = stateToFormNameMap[inheritanceWaiverState];

  const isL8ITWUploaded = taxDocumentUploadState.filesMetaData.some(
    (fileData) => fileData?.isL8ITWUploaded === true,
  );

  if (isNJOrPAEligible(inheritanceWaiverState, currentTransaction)) {
    additionalText = isL8ITWUploaded
      ? `Note: If we have any issues with your ${formName} form, we’ll contact you.`
      : '';
  }

  const L8ITWCheckboxesFiltered = Object.entries(
    taxDocumentUploadState?.checkboxSelections || {},
  ).filter(([key, value]) => value === true);

  const L8ITWCheckedKey: TaxUploadOptionKey | null =
    L8ITWCheckboxesFiltered.length > 0
      ? (L8ITWCheckboxesFiltered[0][0] as TaxUploadOptionKey)
      : null;

  const L8ITWCheckboxSelectionRow = L8ITWCheckedKey
    ? buildL8ITWDocumentsRow(L8ITWCheckedKey, additionalText, formName)
    : null;

  // Build rows array
  const rows: ReceiptRow[] = [];

  if (hasDocumentUploads || hasWldDocuments || hasTaxDocumentUploads) {
    const fileNameValues = documentUploadState.filesMetaData.map(
      (fileMetaData) => fileMetaData.fileName,
    );
    const wldFileNameValues = allWldDocuments.map((wldDocument) =>
      buildWldDocumentDisplayName(wldDocument.documentName, wldDocument.mimeType),
    );
    const taxFileNameValues = taxDocumentUploadState.filesMetaData.map(
      (fileMetaData) => fileMetaData.fileName,
    );

    rows.push({
      label: 'Documents uploaded',
      values: [...fileNameValues, ...wldFileNameValues, ...taxFileNameValues],
      additionalText: '',
      evidenceID: 'docName',
    });
  }

  // Add checkbox selection row if checkboxes are selected
  if (L8ITWCheckboxSelectionRow) {
    rows.push(L8ITWCheckboxSelectionRow);
  }

  // Add additionalText to the last row in the array (before adding L8ITWUploads)
  // This preserves the original behavior where additionalText appears on docName or L8ITWDocuments
  if (rows.length > 0 && additionalText) {
    rows[rows.length - 1].additionalText = additionalText;
  }

  // Add L8ITWUploads row with boolean value indicating if client uploaded L8 file
  // This row is always added exactly once since the rows array is freshly created
  rows.push({
    label: 'L8/ITW Upload Status',
    values: [String(hasClientUploadedL8File)],
    evidenceID: 'L8ITWUploads',
    hideRow: true,
  });

  const receipt: IReceipt = {
    header: 'Legal documents',
    description: null,
    editable: editable,
    editRoute: 'secure/inheritance/upload',
    sectionEvidenceID: 'documentUpload',
    rows: rows,
  };

  return receipt;
}

export function buildDecedentInfoReceipt(
  state: DecedentInfoState,
  triage: TriageState,
  editable: boolean = true,
): IReceipt {
  const isNotInheritanceFlow = !getIsInheritanceFlow(triage) && !getIsMultiInheritanceFlow(triage);

  if (isNotInheritanceFlow) return null;

  const isIRAExecutor = getIsIRASingleExecutor(triage);
  const isDecedentMarried = getIsDecedentMarried(triage);
  const fullName = joinWithSpace([state.firstName, state.middleName, state.lastName]);

  const receipt: IReceipt = {
    header: 'Decedent information',
    description: null,
    editable: editable,
    editRoute: 'secure/inheritance/decedent-info',
    sectionEvidenceID: 'accountOwner',
    rows: [
      {
        label: 'Name',
        values: [fullName],
        evidenceID: 'accountOwnerName',
      },
      {
        label: 'Date of birth',
        values: [state.dateOfBirth],
        evidenceID: 'accountOwnerDob',
      },
    ],
  };

  if (editable) {
    safeRowBuilder(
      receipt.rows,
      'Social Security number',
      state.decedentId_private,
      'accountOwnerClientId-private',
    );
  } else {
    safeRowBuilder(
      receipt.rows,
      'Social Security number',
      '***-**-' + splitClientId(state.decedentId_private),
      'accountOwnerClientId-private',
    );
  }

  receipt.rows.push(
    {
      label: 'Zip code',
      values: [state.zipCode],
      evidenceID: 'accountOwnerZipcode',
    },
    {
      label: 'Date of death',
      values: [state.dateOfDeath],
      evidenceID: 'accountOwnerDod',
    },
  );

  if (isIRAExecutor) {
    receipt.rows.push({
      label: 'Marital status at the time of passing',
      values: [isDecedentMarried ? 'Married' : 'Not married'],
      evidenceID: 'decedentsMaritalStatus',
    });
  }

  return receipt;
}

export function buildEstateReceipt(
  estateState: EstateState,
  triageState: TriageState,
  editable: boolean,
  hideWithholding: boolean = false,
): IReceipt {
  const isNotIRAExecutor = !getIsIRAExecutor(triageState);
  const isNotSingleExecutor = !getIsSingleExecutor(triageState);
  if (isNotIRAExecutor || isNotSingleExecutor) return null;

  const rows: ReceiptRow[] = [
    {
      label: 'Estate name',
      values: [estateState.estateName || ''],
      evidenceID: 'estateName',
    },
  ];

  if (estateState.tin_private) {
    const tin = editable
      ? estateState.tin_private
      : '**-***' + splitClientId(estateState.tin_private);
    rows.push({
      label: 'Estate tax identification number',
      values: [tin],
      evidenceID: 'estatesIdentificationNumber',
    });
  }

  rows.push({
    label: 'Federal tax classification',
    values: ['Estate'],
    evidenceID: 'estateFederalClassIdentification',
  });

  if (estateState.countryQuestion) {
    rows.push({
      label: 'Country of establishment',
      values: [estateState.countryQuestion],
      evidenceID: 'estateCountry',
    });
  }

  if (estateState.taxResidencyQuestion) {
    rows.push({
      label: 'Tax residency',
      values: [estateState.taxResidencyQuestion],
      evidenceID: 'estateTaxResidency',
    });
  }

  if (!hideWithholding && estateState.withholdingQuestion) {
    rows.push({
      label: 'Subject to backup withholding',
      values: [estateState.withholdingQuestion],
      evidenceID: 'subjectToBackupWithHolding',
    });
  }

  const flow = urlMapper[triageState.flowType];

  const receipt: IReceipt = {
    header: 'Estate tax information',
    description: null,
    editable: editable,
    editRoute: `secure/${flow}/estate`,
    sectionEvidenceID: 'estate',
    rows: rows,
  };

  return receipt;
}

export function buildFundingReceipt(
  triageState: TriageState,
  selectedClientAccountsState: ISelectedClientAccountsState,
  editable: boolean,
  openAccountState: IOpenAccountState,
  VCDToggleOn: boolean,
): IReceipt {
  const isNotIRAExecutor = !getIsIRAExecutor(triageState);
  const isNotSingleExecutor = !getIsSingleExecutor(triageState);
  const isNotIRAtoEstate = isNotIRAExecutor || isNotSingleExecutor;
  const isTransactionTypeExisting =
    TransactionTypesEnum.EXISTING === selectedClientAccountsState.transactionType;
  const isNotTrustToTrustFlow = !getIsTrustToTrustFlow(triageState);
  const shouldShowMFESettlementFundSection = VCDToggleOn && !isNotTrustToTrustFlow;
  if (isTransactionTypeExisting || (isNotIRAtoEstate && isNotTrustToTrustFlow)) return null;
  const flow = urlMapper[triageState.flowType];

  const receipt: IReceipt = {
    header: 'Funding',
    description: null,
    editable: editable,
    editRoute: `secure/${flow}/funding`,
    sectionEvidenceID: 'funding',
    rows: [
      {
        label: 'Funding method',
        values: ['Asset transfer'],
        evidenceID: 'fundingMethod',
      },
      {
        label: 'Dividends & capital gains',
        values: ['Reinvest distributions'],
        modal: ReceiptModal.DIVCAPGAINS,
        evidenceID: 'divAndCap',
      },
      buildSettlementFundRow(
        shouldShowMFESettlementFundSection,
        openAccountState?.settlementFundChoice,
      ),
    ],
  };

  return receipt;
}

export function buildTrustInfoReceipt(
  triageState: TriageState,
  trustInfoState: TrustState,
  trustTaxInfoState: TrustTaxInfoState,
  tcfTrustState: TCFTrustState,
  editable: boolean,
  hideWithholding: boolean = false,
): IReceipt {
  const isIRAToTrust = getIsIRATrust(triageState);
  const isTrustToTrustFLow = getIsTrustToTrustFlow(triageState);

  if (!isIRAToTrust && !isTrustToTrustFLow) return null;

  const rows = [];

  if (isIRAToTrust) {
    rows.push(
      {
        label: 'Trust type',
        values: [IRATrustTypesMapper[triageState.typeOfTrust]],
        evidenceID: 'trustType',
      },
      {
        label: 'Trust name',
        values: [trustInfoState.trustName],
        evidenceID: 'trustName',
      },
    );

    if (trustInfoState.trustDate) {
      rows.push({
        label: 'Trust date',
        values: [trustInfoState.trustDate],
        evidenceID: 'trustDate',
      });
    }

    rows.push({
      label: 'Federal tax classification',
      values: [trustInfoState.numberQuestion === TrustNumber.EIN ? 'Trust' : 'Individual'],
      evidenceID: 'trustTaxClassification',
    });

    if (trustInfoState.numberQuestion === TrustNumber.EIN) {
      rows.push({
        label: 'Trust tax identification number',
        values: editable
          ? [trustTaxInfoState.ein + ' (EIN)']
          : ['**-***' + splitClientId(trustTaxInfoState.ein) + ' (EIN)'],
        evidenceID: 'trustTaxIdentificationNumber',
      });
    }
  }

  if (isTrustToTrustFLow) {
    const trustName = trustInfoState.trustName
      ? trustInfoState.trustName
      : tcfTrustState.info.trustName;
    const trustDate = trustInfoState.trustDate
      ? trustInfoState.trustDate
      : tcfTrustState.info.trustDate;

    rows.push(
      {
        label: 'Trust name',
        values: [trustName],
        evidenceID: 'trustName',
      },
      {
        label: 'Trust date',
        values: [dayjs(trustDate).format('MM/DD/YYYY')],
        evidenceID: 'trustDate',
      },
      {
        label: 'Federal tax classification',
        values: [trustTaxInfoState.numberQuestion === TrustNumber.EIN ? 'Trust' : 'Individual'],
        evidenceID: 'trustTaxClassification',
      },
    );

    if (trustTaxInfoState.numberQuestion === TrustNumber.EIN) {
      rows.push({
        label: 'Trust tax identification number',
        values: editable
          ? [trustTaxInfoState.ein + ' (EIN)']
          : ['**-***' + splitClientId(trustTaxInfoState.ein) + ' (EIN)'],
        evidenceID: 'trustTaxIdentificationNumber',
      });
    }
  }

  const trustAddress = trustInfoState.trustAddress
    ? ['Same as trustee']
    : [
        trustInfoState.address,
        `${trustInfoState.city}, ${trustInfoState.state} ${trustInfoState.zipCode}`,
      ];

  const mailingAddress = trustInfoState.mailingAddress
    ? ['Same as trust']
    : [
        trustInfoState.mailAddress,
        `${trustInfoState.mailCity}, ${trustInfoState.mailState} ${trustInfoState.mailZipCode}`,
      ];

  rows.push(
    {
      label: 'Trust address',
      values: trustAddress,
      evidenceID: 'trustAddress',
    },
    {
      label: 'Trust mailing address',
      values: mailingAddress,
      evidenceID: 'trustMailingAddress',
    },
  );

  if (
    trustInfoState.numberQuestion === TrustNumber.SSN ||
    trustTaxInfoState.numberQuestion === TrustNumber.SSN
  ) {
    rows.push(
      {
        label: 'Grantor',
        values: [trustTaxInfoState.grantorName],
        evidenceID: 'trustGrantorName',
      },
      {
        label: 'Grantor Social Security number',
        values: editable
          ? [trustTaxInfoState.ssn]
          : ['***-**-' + splitClientId(trustTaxInfoState.ssn)],
        evidenceID: 'trustGrantorSSN',
      },
    );
  }

  if (trustInfoState.numberQuestion === TrustNumber.SSN) {
    rows.push({
      label: 'Grantor citizenship',
      values: [trustInfoState.grantorQuestion === CountryUS.US ? 'U.S. citizen' : 'Other'],
      evidenceID: 'trustGrantorName',
    });
  }

  rows.push(
    {
      label: 'Country of establishment',
      values: [trustInfoState.countryQuestion],
      evidenceID: 'establishmentCountry',
    },
    {
      label: 'Tax residency',
      values: [trustInfoState.taxResidencyQuestion],
      evidenceID: 'taxResidency',
    },
  );

  if (!hideWithholding) {
    rows.push({
      label: 'Subject to backup withholding',
      values: [trustTaxInfoState.withholdingQuestion],
      evidenceID: 'backupWithholding',
    });
  }

  const flow = urlMapper[triageState.flowType];

  const receipt: IReceipt = {
    header: 'Trust information',
    description: null,
    editable: editable,
    editRoute: `secure/${flow}/trust-info`,
    sectionEvidenceID: 'trustInfo',
    rows: rows,
  };

  return receipt;
}

export function buildTrusteeReceipt(
  triageState: TriageState,
  tcfTrustState: TCFTrustState,
  isRemovingTrustee: boolean,
  editable: boolean,
): IReceipt {
  const isTrustToTrustFlow = getIsTrustToTrustFlow(triageState);
  const trusteeInformation = getTCFTrusteeInformation(tcfTrustState);
  const trustee = trusteeInformation.find((trustee) => trustee.isRemove === isRemovingTrustee);
  const prefix = isRemovingTrustee ? 'removed' : 'added';

  if (!isTrustToTrustFlow || !trustee) return null;

  const rows = [
    {
      label: 'Name',
      values: [trustee.trusteeName],
      evidenceID: prefix + 'TrusteeName',
    },
    {
      label: 'Social Security number',
      values: editable ? [trustee.ssn] : ['***-**-' + splitClientId(trustee.ssn)],
      evidenceID: prefix + 'TrusteeSSN',
    },
  ];

  const flow = urlMapper[triageState.flowType];

  const receipt: IReceipt = {
    header: isRemovingTrustee ? 'Removed trustee' : 'New trustee',
    description: null,
    editable: editable,
    editRoute: `secure/${flow}/tcf-info-trust`,
    sectionEvidenceID: prefix + 'Trustee',
    rows: rows,
  };

  return receipt;
}

export function buildAccountFromTrust(tcfTrustState: TCFTrustState, editable: boolean) {
  const { trustName, trustDate } = tcfTrustState.info;

  const receipt: IReceipt = {
    header: 'Transferring from',
    editable: false,
    editRoute: '',
    sectionEvidenceID: 'accountFrom',
    rows: [],
  };

  receipt.rows.push(
    {
      label: 'Account type',
      values: ['Trust brokerage account'],
      evidenceID: 'accountType',
    },
    {
      label: 'Source of Funds',
      values: ['Family/Inheritance'],
      evidenceID: 'sourceOfFunds',
    },
    {
      label: 'Trust name',
      values: [trustName],
      evidenceID: 'trustName',
    },
    {
      label: 'Trust date',
      values: [dayjs(trustDate).format('MM/DD/YYYY')],
      evidenceID: 'trustDate',
    },
  );
  return receipt;
}

export function buildAccountToTrust() {
  const receipt: IReceipt = {
    header: 'Transferring to',
    editable: false,
    editRoute: '',
    sectionEvidenceID: 'accountTo',
    rows: [],
  };

  receipt.rows.push({
    label: 'Account type',
    values: ['Trust brokerage account'],
    evidenceID: 'toAccountType',
  });

  return receipt;
}
